import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import DsButton from '../../common/DsButton';
import DsModal from '../../common/DsModal';
import { useToast } from '../../common/ToastSystem';
import '../../../css/admin/settings/AdminFullNameRequiredModal.css';
import { getCurrentAdminProfile, updateCurrentAdminFullName } from '../../../api/profiles';

function AdminFullNameRequiredModal() {
  const location = useLocation();
  const { user, fetchSession } = useAuth();
  const { success, error: toastError } = useToast();

  const [needsFullName, setNeedsFullName] = useState(false);
  const [checking, setChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fullName, setFullName] = useState('');
  const [formError, setFormError] = useState('');

  const isAdmin = Boolean(user?.isLoggedIn && user?.userType === 'admin' && user?.userId);
  const isAdminSurface = useMemo(
    () => {
      const path = String(location.pathname || '');
      return path.startsWith('/admin/workspaces') || path.startsWith('/admin/settings');
    },
    [location.pathname],
  );
  const sessionFullName = String(user?.full_name || '').trim();

  useEffect(() => {
    if (!isAdmin || !isAdminSurface) {
      setNeedsFullName(false);
      setFormError('');
      return;
    }
    if (sessionFullName) {
      setNeedsFullName(false);
      return;
    }
    if (needsFullName) return;

    let cancelled = false;
    (async () => {
      setChecking(true);
      try {
        const data = await getCurrentAdminProfile();
        const currentFullName = String(data?.full_name || '').trim();
        if (cancelled) return;
        if (currentFullName) {
          await fetchSession();
          setNeedsFullName(false);
          return;
        }
        setNeedsFullName(true);
      } catch (_) {
        if (!cancelled) {
          setNeedsFullName(true);
        }
      } finally {
        if (!cancelled) {
          setChecking(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [fetchSession, isAdmin, isAdminSurface, needsFullName, sessionFullName]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError('');
    const trimmed = String(fullName || '').trim();
    if (!trimmed) {
      setFormError('フルネームを入力してください。');
      return;
    }

    setSubmitting(true);
    try {
      await updateCurrentAdminFullName({ full_name: trimmed });
      await fetchSession();
      setNeedsFullName(false);
      setFullName('');
      success('フルネームを登録しました。');
    } catch (error) {
      const message = error?.message || 'フルネームの登録に失敗しました。';
      setFormError(message);
      toastError(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isAdmin || !isAdminSurface) return null;
  if (checking && !needsFullName) return null;
  if (!needsFullName) return null;

  return (
    <DsModal
      open
      onClose={() => {}}
      title="フルネーム登録が必要です"
      size="sm"
      dismissible={false}
      showClose={false}
    >
      <p className="admin-fullname-required__lead">
        フルネームはアーティストに表示されます。登録が完了するまで管理者機能は利用できません。
      </p>

      <form onSubmit={handleSubmit} className="admin-fullname-required__form">
        <label htmlFor="admin-fullname-required-input">フルネーム</label>
        <input
          id="admin-fullname-required-input"
          type="text"
          maxLength={80}
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          placeholder="例: 山田 太郎"
          autoComplete="name"
          disabled={submitting}
          required
        />
        {formError ? <p className="admin-fullname-required__error">{formError}</p> : null}
        <DsButton type="submit" disabled={submitting}>
          {submitting ? '登録中…' : 'フルネームを登録'}
        </DsButton>
      </form>
    </DsModal>
  );
}

export default AdminFullNameRequiredModal;
